"use client";

const CSRF_COOKIE_NAME = "artpass_csrf";
const CSRF_HEADER_NAME = "x-csrf-token";

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, data: unknown, message?: string) {
    super(message ?? `Request failed with status ${status}`);
    this.status = status;
    this.data = data;
  }
}

/** fetch wrapper that attaches the double-submit CSRF header for mutating requests. */
export async function apiFetch<T = unknown>(
  input: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  const body = init.json !== undefined ? JSON.stringify(init.json) : init.body;

  if (init.json !== undefined) headers.set("content-type", "application/json");
  if (method !== "GET" && method !== "HEAD") {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) headers.set(CSRF_HEADER_NAME, csrf);
  }

  const res = await fetch(input, { ...init, method, headers, body, credentials: "same-origin" });
  const contentType = res.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, data, extractErrorMessage(data));
  }
  return data as T;
}

/** Prefer the human-readable `detail` field our API routes send; fall back to the error code. */
function extractErrorMessage(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const record = data as Record<string, unknown>;
  if (typeof record.detail === "string") return record.detail;
  if (typeof record.error === "string") return record.error;
  return undefined;
}

/**
 * Standard catch-block error text: an ApiError's message is already human-readable
 * (extractErrorMessage above), and any other real Error (network failure, an API not
 * available in the current browser context, etc.) is more useful shown as-is than hidden
 * behind a generic fallback string.
 */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
